import React, { useReducer, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Upload, Columns, Loader2, Table, CheckCircle2 } from 'lucide-react';

import FileDropZone from './FileDropZone';
import MappingPreview from './MappingPreview';
import ColumnMapper from './ColumnMapper';
import ProcessingView from './ProcessingView';
import ResultsTable from './ResultsTable';
import SaveConfirmation from './SaveConfirmation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: 'upload', label: 'Upload', icon: Upload },
  { key: 'uploaded', label: 'Uploaded', icon: CheckCircle2 },
  { key: 'mapping', label: 'Mapping', icon: Columns },
  { key: 'processing', label: 'Processing', icon: Loader2 },
  { key: 'review', label: 'Review', icon: Table },
  { key: 'saved', label: 'Saved', icon: CheckCircle2 },
];

const DEFAULT_VEHICLE_TYPES = {
  road: 'hgv_all_hgvs_average',
  rail: 'freight_train',
  air: 'international_with_rf', // DESNZ "International, to/from non-UK"
  water: 'cargo_container_ship_average',
};

// Freight fields carried into the one-click calculation. fuel_type was missing, so a mapped fuel
// column (petrol / CNG vans) was silently dropped and every van priced as diesel.
const ALL_FIELDS = ['origin', 'destination', 'weight', 'transport_mode', 'vehicle_type', 'fuel_type', 'good_name', 'cost_value', 'ghg_category'];

const SCOPE3_FIELDS = [
  'mode', 'activity_value', 'activity_unit', 'origin', 'destination',
  'vehicle_type', 'cabin_class', 'radiative_forcing', 'fuel_type', 'size_class',
  'passengers', 'region', 'travel_date', 'purpose', 'employee_ref',
  'days_per_week', 'weeks_in_period', 'wfh_days_per_week', 'employee_id',
  'one_way_distance', 'home_address', 'work_address',
];

const initialState = {
  step: 'upload',
  isUploading: false,
  isCalculating: false,
  isSaving: false,
  resultsLoading: false,
  sessionId: null,
  filename: null,
  totalRows: 0,
  columns: [],
  sampleRows: [],
  proposedMapping: null,
  confirmedMapping: null,
  uploadWarnings: [],
  streamTicket: null,
  results: [],
  summary: null,
  saveResult: null,
  // AI auto-detection fields
  autoReady: false,
  vehiclePreview: {},
  detectedDefaults: {},
  // Vehicle editing
  vehicleOverrides: {},
  vehicleFuelOverrides: {},
  vehicleOptions: null,
  // Remember this upload's vehicle corrections for the user's future uploads (per user only).
  rememberOverrides: true,
};

function reducer(state, action) {
  switch (action.type) {
    case 'UPLOADING':
      return { ...state, isUploading: true };
    case 'UPLOAD_SUCCESS':
      return {
        ...state,
        isUploading: false,
        step: 'uploaded',
        sessionId: action.payload.session_id,
        filename: action.payload.filename,
        totalRows: action.payload.total_rows,
        columns: action.payload.columns,
        sampleRows: action.payload.sample_rows,
        proposedMapping: action.payload.proposed_mapping,
        uploadWarnings: action.payload.warnings || [],
        autoReady: action.payload.auto_ready || false,
        vehiclePreview: action.payload.vehicle_preview || {},
        detectedDefaults: action.payload.detected_defaults || {},
      };
    case 'UPLOAD_ERROR':
      return { ...state, isUploading: false };
    case 'GO_TO_MAPPING':
      return { ...state, step: 'mapping' };
    case 'CALCULATING_START':
      return { ...state, isCalculating: true };
    case 'CALCULATING':
      return { ...state, isCalculating: false, step: 'processing', streamTicket: action.payload?.stream_ticket || null };
    case 'CALCULATING_ERROR':
      return { ...state, isCalculating: false };
    case 'CALC_COMPLETE':
      return { ...state, summary: action.payload, resultsLoading: true };
    case 'RESULTS_LOADED':
      return { ...state, results: action.payload, resultsLoading: false, step: 'review' };
    case 'RESULTS_LOAD_ERROR':
      return { ...state, resultsLoading: false, step: 'review' };
    case 'SAVING':
      return { ...state, isSaving: true };
    case 'SAVED':
      return { ...state, isSaving: false, step: 'saved', saveResult: action.payload };
    case 'SAVE_ERROR':
      return { ...state, isSaving: false };
    case 'STORE_MAPPING':
      return { ...state, confirmedMapping: action.payload };
    case 'SET_VEHICLE_OPTIONS':
      return { ...state, vehicleOptions: action.payload };
    case 'SET_REMEMBER_OVERRIDES':
      return { ...state, rememberOverrides: action.payload };
    case 'SET_VEHICLE_OVERRIDE':
      return {
        ...state,
        vehicleOverrides: {
          ...state.vehicleOverrides,
          [action.payload.vehicleText.toLowerCase()]: action.payload.vehicleKey,
        },
        vehicleFuelOverrides: action.payload.fuelType
          ? {
              ...state.vehicleFuelOverrides,
              [action.payload.vehicleText.toLowerCase()]: action.payload.fuelType,
            }
          : state.vehicleFuelOverrides,
        vehiclePreview: {
          ...state.vehiclePreview,
          [action.payload.vehicleText]: {
            ...state.vehiclePreview[action.payload.vehicleText],
            resolved_key: action.payload.vehicleKey,
            display_name: action.payload.displayName,
            tier: 0,
          },
        },
      };
    case 'RESET':
      return { ...initialState };
    case 'GO_BACK':
      return { ...state, step: action.payload };
    default:
      return state;
  }
}

/**
 * @param {object} props
 * @param {'freight'|'business_travel'|'employee_commute'} [props.activityType]
 */
export default function AgentUpload({
  onGoToDashboard,
  onShipmentsSaved,
  onOpenInSingleShipment,
  activityType = 'freight',
}) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Step 1: Upload file
  const handleFileSelected = useCallback(async (file) => {
    dispatch({ type: 'UPLOADING' });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('activity_type', activityType);

    try {
      const res = await axios.post(`${API}/agent/upload`, formData);
      dispatch({ type: 'UPLOAD_SUCCESS', payload: res.data });
      toast.success(`Parsed ${res.data.total_rows} rows from ${res.data.filename} (${activityType})`);

      // Show warnings from backend (LLM unavailable, Google Maps not configured, etc.)
      if (res.data.warnings?.length > 0) {
        res.data.warnings.forEach((w) => toast.warning(w, { duration: 8000 }));
      }

      // Fetch vehicle options for editing UI
      try {
        const optionsRes = await axios.get(`${API}/agent/vehicle-options`);
        dispatch({ type: 'SET_VEHICLE_OPTIONS', payload: optionsRes.data });
      } catch (optErr) {
        console.warn('Could not load vehicle options:', optErr);
      }
    } catch (err) {
      dispatch({ type: 'UPLOAD_ERROR' });
      const status = err.response?.status;
      const detail = err.response?.data?.detail;

      console.error('Agent upload failed:', status, detail || err.message);

      if (status === 400 && detail) {
        // File validation or parse errors
        toast.error(`Upload Error: ${detail}`, { duration: 6000 });
      } else if (status === 401 || status === 403) {
        toast.error('Session expired. Please log in again.');
      } else if (status === 429) {
        toast.error('Too many uploads. Please wait a moment and try again.');
      } else if (!err.response) {
        if (err.code === 'ECONNABORTED') {
          toast.error('Upload timed out. The server is taking too long to respond.');
        } else {
          toast.error(`Cannot reach the server: ${err.message || 'Network error'}. Is the backend running?`);
        }
      } else {
        toast.error(detail || 'Upload failed. Please try again.');
      }
    }
  }, [activityType]);

  // Step 2: Confirm mapping & start calculation
  const handleMappingConfirm = useCallback(async (mappingData) => {
    if (!state.sessionId) {
      toast.error('No active session. Please upload your file again.');
      return;
    }
    dispatch({ type: 'CALCULATING_START' });
    dispatch({ type: 'STORE_MAPPING', payload: mappingData });

    const calcUrl = `${API}/agent/calculate`;
    const requestBody = {
      session_id: state.sessionId,
      confirmed_mapping: mappingData.confirmed_mapping,
      default_fuel_type: mappingData.default_fuel_type,
      default_vehicle_types: mappingData.default_vehicle_types,
      vehicle_overrides: mappingData.vehicle_overrides || {},
      vehicle_fuel_overrides: mappingData.vehicle_fuel_overrides || {},
      activity_type: activityType,
      remember_vehicle_overrides: !!state.rememberOverrides,
    };

    // Use fetch (not axios) to bypass interceptor chain and get cleaner CORS diagnostics
    const token = localStorage.getItem('token');
    const fetchHeaders = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };

    // Pre-flight: test a simple authenticated JSON POST to verify CORS works
    try {
      const pingResp = await fetch(`${API}/agent/ping`, {
        method: 'POST',
        headers: fetchHeaders,
        body: '{}',
      });
      console.log('[Calculate] CORS ping test:', pingResp.status, pingResp.ok ? 'OK' : 'FAILED');
      if (!pingResp.ok && pingResp.status === 401) {
        toast.error('Session expired. Please log in again.');
        dispatch({ type: 'CALCULATING_ERROR' });
        return;
      }
    } catch (pingErr) {
      console.error('[Calculate] CORS ping failed — JSON POST requests are blocked:', pingErr);
      toast.error('Cannot send JSON requests to the backend (CORS issue). Check browser Network tab for blocked OPTIONS preflight.', { duration: 10000 });
      dispatch({ type: 'CALCULATING_ERROR' });
      return;
    }

    // Actual calculate call
    try {
      const resp = await fetch(calcUrl, {
        method: 'POST',
        headers: fetchHeaders,
        body: JSON.stringify(requestBody),
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const detail = errorData.detail;

        if (resp.status === 429) {
          toast.error('Rate limit reached. Please wait before starting another calculation.');
        } else if (resp.status === 404) {
          toast.error('Session expired. Please upload your file again.');
        } else if (resp.status === 422) {
          const msg = Array.isArray(detail)
            ? detail.map(e => e.msg || e.message).join('; ')
            : detail || 'Invalid request data';
          toast.error(`Validation error: ${msg}`);
        } else {
          toast.error(detail || `Calculation failed (${resp.status}). Please try again.`);
        }
        dispatch({ type: 'CALCULATING_ERROR' });
        return;
      }

      const data = await resp.json();
      dispatch({ type: 'CALCULATING', payload: data });
    } catch (err) {
      dispatch({ type: 'CALCULATING_ERROR' });
      console.error('[Calculate] fetch failed:', err.message, err);
      toast.error(`Network error on /calculate: ${err.message}. CORS ping passed, so this may be a backend crash. Check server logs.`, { duration: 8000 });
    }
  }, [state.sessionId, state.rememberOverrides, activityType]);

  // Step 2b: Auto-calculate from MappingPreview (builds confirmed mapping from AI proposal)
  const handleAutoCalculate = useCallback(() => {
    const pm = state.proposedMapping;
    if (!pm) return;

    const isScope3 = activityType === 'business_travel' || activityType === 'employee_commute';
    const fields = isScope3 ? SCOPE3_FIELDS : ALL_FIELDS;

    const confirmed = {};
    for (const field of fields) {
      if (pm[field]?.column) confirmed[field] = pm[field].column;
    }
    if (!isScope3) {
      // proposedMapping.weight_unit is the server's reconciled unit (header → AI → kg). The old
      // order let a value-size guess override it — a 1000x error either way.
      confirmed.weight_unit = pm?.weight_unit || state.detectedDefaults?.weight_unit || 'kg';
      confirmed.cost_type = pm?.cost_type || 'total';
      confirmed.ghg_category = state.detectedDefaults?.ghg_category || 'upstream';
    }

    const mappingData = {
      confirmed_mapping: confirmed,
      default_fuel_type: 'diesel',
      default_vehicle_types: DEFAULT_VEHICLE_TYPES,
      vehicle_overrides: state.vehicleOverrides,
      vehicle_fuel_overrides: state.vehicleFuelOverrides,
    };
    handleMappingConfirm(mappingData);
  }, [state.proposedMapping, state.detectedDefaults, state.vehicleOverrides, state.vehicleFuelOverrides, handleMappingConfirm, activityType]);

  // Step 3: Processing complete — load full results before transitioning
  const handleProcessingComplete = useCallback(async (summary) => {
    dispatch({ type: 'CALC_COMPLETE', payload: summary });

    try {
      const res = await axios.get(`${API}/agent/results/${state.sessionId}`);
      dispatch({ type: 'RESULTS_LOADED', payload: res.data.results });
    } catch (err) {
      toast.error('Failed to load results');
      dispatch({ type: 'RESULTS_LOAD_ERROR' });
    }
  }, [state.sessionId]);

  // Step 4: Save selected rows
  const handleSave = useCallback(async (selectedRows) => {
    dispatch({ type: 'SAVING' });
    try {
      const res = await axios.post(`${API}/agent/save`, {
        session_id: state.sessionId,
        selected_rows: selectedRows,
      });
      const { saved = 0, skipped = 0, errors = [] } = res.data || {};

      // Refresh dashboard data BEFORE flipping to the 'saved' step, so the
      // always-visible Dashboard section reflects the new rows by the time
      // SaveConfirmation is shown. If we skipped this, users would see
      // "N shipments saved" but no new rows appear until the next refresh.
      if (onShipmentsSaved) {
        try {
          await onShipmentsSaved();
        } catch (refreshErr) {
          console.error('Dashboard refresh after save failed:', refreshErr);
        }
      }

      dispatch({ type: 'SAVED', payload: res.data });

      if (saved > 0) {
        toast.success(`${saved} shipment${saved === 1 ? '' : 's'} saved to dashboard`);
        if (skipped > 0) {
          toast.warning(
            `${skipped} row${skipped === 1 ? '' : 's'} skipped. ${errors[0] || 'See details above.'}`,
            { duration: 8000 },
          );
        }
      } else {
        // saved === 0: nothing hit the dashboard. Surface the real reason so
        // the user doesn't think it worked silently.
        const firstErr = errors[0] || 'No rows were saved.';
        toast.error(`Save failed: ${firstErr}`, { duration: 10000 });
        if (errors.length > 1) {
          console.error('Bulk save errors:', errors);
        }
      }
    } catch (err) {
      dispatch({ type: 'SAVE_ERROR' });
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403) {
        toast.error(detail || 'Calculation limit reached. Ask your admin to raise your limit.', { duration: 10000 });
      } else if (status === 400) {
        toast.error(detail || 'Save rejected — check remaining calculations or session state.');
      } else {
        toast.error(detail || `Save failed${status ? ` (${status})` : ''}. Please try again.`);
      }
    }
  }, [state.sessionId, onShipmentsSaved]);

  // Determine current step index for stepper (skip 'uploaded' in display)
  const displaySteps = STEPS.filter((s) => s.key !== 'uploaded');
  const displayStepKey = state.step === 'uploaded' ? 'upload' : state.step;
  const currentStepIdx = displaySteps.findIndex((s) => s.key === displayStepKey);
  // 'uploaded' counts as done for the upload step
  const uploadDone = ['uploaded', 'mapping', 'processing', 'review', 'saved'].includes(state.step);

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-1">
        {displaySteps.map((s, i) => {
          const Icon = s.icon;
          const isActive = i === currentStepIdx;
          const isDone = state.step === 'uploaded' && i === 0 ? true : i < currentStepIdx;

          return (
            <div key={s.key} className="flex items-center">
              <div
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  isActive && !isDone
                    ? 'bg-sky-100 text-sky-700'
                    : isDone
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {isDone ? (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                ) : (
                  <Icon className="w-3.5 h-3.5" />
                )}
                {s.label}
              </div>
              {i < displaySteps.length - 1 && (
                <div className={`w-6 h-0.5 mx-1 ${isDone ? 'bg-green-300' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* File info badge */}
      {state.filename && !['upload', 'uploaded'].includes(state.step) && (
        <div className="flex items-center gap-2 justify-center">
          <Badge variant="outline" className="text-xs">
            {state.filename} — {state.totalRows} rows
          </Badge>
        </div>
      )}

      {/* Step Content */}
      {state.step === 'upload' && (
        <FileDropZone
          onFileSelected={handleFileSelected}
          isUploading={state.isUploading}
          activityType={activityType}
        />
      )}

      {state.step === 'uploaded' && (
        <MappingPreview
          filename={state.filename}
          totalRows={state.totalRows}
          columns={state.columns}
          proposedMapping={state.proposedMapping}
          vehiclePreview={state.vehiclePreview}
          detectedDefaults={state.detectedDefaults}
          autoReady={state.autoReady}
          uploadWarnings={state.uploadWarnings}
          isCalculating={state.isCalculating}
          onCalculate={handleAutoCalculate}
          onEditMapping={() => dispatch({ type: 'GO_TO_MAPPING' })}
          onReset={() => dispatch({ type: 'RESET' })}
          vehicleOptions={state.vehicleOptions}
          vehicleFuelOverrides={state.vehicleFuelOverrides}
          onVehicleOverride={(vehicleText, vehicleKey, displayName, fuelType) =>
            dispatch({ type: 'SET_VEHICLE_OVERRIDE', payload: { vehicleText, vehicleKey, displayName, fuelType } })
          }
          rememberOverrides={state.rememberOverrides}
          onRememberChange={(v) => dispatch({ type: 'SET_REMEMBER_OVERRIDES', payload: v })}
        />
      )}

      {state.step === 'mapping' && (
        <ColumnMapper
          columns={state.columns}
          sampleRows={state.sampleRows}
          proposedMapping={state.proposedMapping}
          confirmedMapping={state.confirmedMapping}
          isSubmitting={state.isCalculating}
          onConfirm={handleMappingConfirm}
          onBack={() => dispatch({ type: 'GO_BACK', payload: 'uploaded' })}
        />
      )}

      {state.step === 'processing' && (
        <ProcessingView
          sessionId={state.sessionId}
          totalRows={state.totalRows}
          streamTicket={state.streamTicket}
          resultsLoading={state.resultsLoading}
          onComplete={handleProcessingComplete}
          onError={(err) => toast.error(err)}
          onReset={() => dispatch({ type: 'RESET' })}
        />
      )}

      {state.step === 'review' && (
        <ResultsTable
          results={state.results}
          summary={state.summary}
          sessionId={state.sessionId}
          isSaving={state.isSaving}
          onSave={handleSave}
          onBack={() => dispatch({ type: 'GO_BACK', payload: 'mapping' })}
          onOpenInSingleShipment={onOpenInSingleShipment}
        />
      )}

      {state.step === 'saved' && (
        <SaveConfirmation
          saveResult={state.saveResult}
          onNewUpload={() => dispatch({ type: 'RESET' })}
          onGoToDashboard={onGoToDashboard}
        />
      )}
    </div>
  );
}
